// @ts-nocheck
import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  useTheme,
  Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import { ReactComponent as CloseIcon } from '../../../../assets/Icons/lucideIcons/x.svg';
import { ReactComponent as VisibilityIcon } from '../../../../assets/Icons/lucideIcons/eye.svg';
import { ReactComponent as EditIcon } from '../../../../assets/Icons/lucideIcons/pencil.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/Icons/lucideIcons/trash-2.svg';
import { useContextMenu } from 'react-contexify';
import { useSelector } from 'react-redux';

import CreateEditSegmentModal from '../../../ModelInventory/ModelSummary/CreateEditSegmentModal';
import { extractAllAttributesFromTemplate } from '../../../../features/configuration/template/components/SectionAndAttribute/commonFunction';
import useTemplate from '../../../../features/configuration/template/hooks/useTemplate';
import ReactMuiTableListView from '../../ReactMuiTableListViewV8';
import {
  filterSectionsByPermissions,
  isValueEmpty,
} from '../../../../utils/Utils';
import useSegments from '../../../../hooks/useSegments';
import { ContextMenus } from '../../ContextMenus';
import DeleteModal from '../../Modals/DeleteModal';
import SwalToast from '../../swalToast';
import { useAttributeListQuery } from '../../../../features/configuration/attributeLibrary/api/attributeQueries';
import {
  DEFAULT_SEGMENT_ID,
  EMPTY_OBJECT,
} from '../../../../const/CommonConst';
import useSectionPermissions from '../../../../hooks/ModelInventory/useSectionPermissions';
import useModelInventory from '../../../../hooks/ModelInventory/useModelInventory';
import useAttribute from '../../../../hooks/ModelInventory/useAttribute';
import useCommon from '../../../../hooks/useCommon';
import { useSingleWorkflowQuery } from '../../../../features/configuration/workflow/api/workflowQueries';

const renderDescriptionCell = ({ row }) => {
  if (!isValueEmpty(row?.original?.description)) {
    return row?.original?.description;
  }
  return '-';
};
const renderJustificationCell = ({ row }) => {
  if (!isValueEmpty(row?.original?.justification)) {
    return row?.original?.justification;
  }
  return '-';
};
const renderActionCell = ({ row }) => {
  if (row?.original?.is_updated) {
    return 'Update';
  }
  if (!isValueEmpty(row?.original?.replace_segment_id)) {
    return 'Replace';
  }
  return 'Add';
};

const SEGMENTS_COLUMNS = [
  {
    Header: 'Segment Name',
    accessor: 'segment_name',
    disableSortBy: false,
    width: 100,
  },
  {
    Header: 'Description',
    accessor: 'description',
    disableSortBy: false,
    width: 100,
    Cell: renderDescriptionCell,
  },
  {
    Header: 'Justification',
    accessor: 'justification',
    disableSortBy: false,
    width: 100,
    Cell: renderJustificationCell,
  },
  {
    Header: 'Action',
    accessor: 'action_type',
    disableSortBy: false,
    width: 100,
    Cell: renderActionCell,
  },
];

const excludedDataTypes = [
  'Chart',
  'Table',
  'AdvancedTable',
  'Association',
  'Document',
  'IssueType',
];

const SegmentsAttribute = ({
  label,
  showLabel = true,
  attribute,
  value,
  onChange,
  entityDetailsFormikForm,
  entityData,
  // Other props required by CreateEditSegmentModal
  allUserList,
  teamUsersList,
  groupList,
  isUserAuthorizedForUpdateAttribute,
  breadcrumbDetails,
  getOverrideAttributeValue,
  deleteNote,
  editNote,
  handleOpenNoteModal,
  handleSetAddNoteModalHeading,
  handleSetSelectedEntityToAddInEditor,
  getAttComments,
  allAttributeChats,
  isMultipleEditMode,
  viewOnly = false,
  hasDraftChip = false,
  onDraftBlur,
}) => {
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [fetchedFormDetails, setFetchedFormDetails] = useState(null);
  const [formDetails, setFormDetails] = useState(null);
  const [fetchedSegments, setFetchedSegments] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [segmentToDelete, setSegmentToDelete] = useState(null);
  const [tempSegments, setTempSegments] = useState([]);
  const [selectedEntityDetails, setSelectedEntityDetails] = useState(null);
  const [modelAttributeDetails, setModelAttributeDetails] = useState({});

  const { dispatch, setCurrentWorkflowGroupId } = useCommon();
  const { getCustomEntityTemplate } = useTemplate();
  const { getSingleEntityDetails } = useModelInventory();
  const { handleGetSectionPermission } = useSectionPermissions();
  const { getSegments } = useSegments();
  const { getAttributeDetails } = useAttribute();
  const { show: showRowLevelMenus, hideAll } = useContextMenu();
  const theme = useTheme();
  const { execute: fetchAttributeList } = useAttributeListQuery({
    options: { enabled: false, loader: false },
  });
  const { data: selectedEntityWorkflowDetails, execute: getSingleWorkflow } =
    useSingleWorkflowQuery({
      options: {
        enabled: false, // don't fetch on mount, we'll call when needed
      },
    });
  // Ref to track the previous entity ID to prevent redundant fetches
  const prevEntityIdRef = useRef(null);

  const selectedEntityKey = useMemo(() => {
    if (!selectedEntityDetails) return null;
    return `${selectedEntityDetails?.entity_id}`;
  }, [selectedEntityDetails]);

  // workflow groups for the selected entity
  const selectedEntityWorkflowGroups = useSelector(
    (state) =>
      state?.common?.workflowGroupsVar?.[selectedEntityKey]
        ?.entityWorkflowGroups
  );
  const sectionPermissions = useSelector(
    (state) => state?.common?.sectionPermissions?.[selectedEntityKey]
  );
  const entityCurrentGroupId = useSelector(
    (state) =>
      state?.common?.workflowGroupsVar?.[selectedEntityKey]
        ?.entityCurrentGroupId
  );

  const filteredFormDetails = useMemo(() => {
    const details = fetchedFormDetails;
    if (!details) {
      return details;
    }

    const filterAttributes = (attributes) => {
      return attributes?.filter(
        (attr) =>
          attr?.attribute_origin !== 'common' &&
          !excludedDataTypes.includes(attr?.data_type)
      );
    };

    const filteredSections = filterSectionsByPermissions({
      sections: details?.sections || [],
      sectionPermissions,
      entityCurrentGroupId,
    });
    // Then filter common attributes from the filtered sections
    return {
      ...details,
      sections:
        filteredSections?.map((section) => ({
          ...section,
          attributes: filterAttributes(section?.attributes || []),
        })) || [],
      attributes: filterAttributes(details?.attributes || []),
    };
  }, [fetchedFormDetails, entityCurrentGroupId, sectionPermissions]);

  // Find the linked attribute definition
  const linkedAttribute = useMemo(() => {
    if (!formDetails) return null;
    const allAttributes = extractAllAttributesFromTemplate(formDetails);
    if (attribute?.linked_attribute) {
      return allAttributes?.find(
        (attr) => attr?.id == attribute?.linked_attribute
      );
    }
    return null;
  }, [formDetails, attribute]);

  const segmentList = useMemo(() => {
    if (!value) return [];
    let parsedValue = value;
    if (typeof value === 'string') {
      try {
        parsedValue = JSON.parse(value);
      } catch (_e) {
        // ignore
      }
    }
    return parsedValue?.segmentTable || [];
  }, [value]);

  // Find "Select Entity" attribute to get the entity key/ID
  const selectEntityValue = useMemo(() => {
    if (!formDetails || !entityDetailsFormikForm?.values) return null;

    if (linkedAttribute) {
      return entityDetailsFormikForm?.values[linkedAttribute?.id];
    }
    return null;
  }, [formDetails, entityDetailsFormikForm, linkedAttribute, viewOnly]);

  const handleFieldClick = useCallback(async () => {
    setTempSegments(segmentList);
    if (viewOnly) {
      setIsActionModalOpen(true);
      return;
    }
    if (!selectEntityValue) {
      if (linkedAttribute) {
        SwalToast({
          title: `Please add value in "${linkedAttribute?.label || 'attribute'}"`,
          icon: 'warning',
        });
        return;
      }
      let attrLabel = 'attribute';

      try {
        const data = await fetchAttributeList({
          params: { is_template: true },
        });
        if (data) {
          const foundAttr = data.find(
            (attr) => attr.id == attribute.linked_attribute
          );
          if (foundAttr) {
            attrLabel = foundAttr.label;
          }
        }
      } catch (_e) {
        // ignore
      }

      SwalToast({
        title: `Please add "${attrLabel || 'Linked'}" attribute in the template.`,
        icon: 'warning',
      });
      return;
    }
    setIsActionModalOpen(true);
  }, [
    viewOnly,
    selectEntityValue,
    attribute?.linked_attribute,
    segmentList,
    linkedAttribute,
    fetchAttributeList,
  ]);

  const handleCloseActionModal = useCallback(() => {
    setIsActionModalOpen(false);
  }, [setIsActionModalOpen]);

  const handleAddSegment = useCallback(() => {
    handleCloseActionModal();
    setModalMode('create');
    setSelectedSegment(null);
    setIsCreateModalOpen(true);
  }, [
    handleCloseActionModal,
    setModalMode,
    setSelectedSegment,
    setIsCreateModalOpen,
  ]);

  const handleReplaceSegment = useCallback(() => {
    handleCloseActionModal();
    setModalMode('replace');
    setSelectedSegment(null);
    setIsCreateModalOpen(true);
  }, [
    handleCloseActionModal,
    setModalMode,
    setSelectedSegment,
    setIsCreateModalOpen,
  ]);

  const { segmentCreationNode, workflowGroupOfSegmentCreationNode } =
    useMemo(() => {
      if (!selectedEntityWorkflowDetails) {
        return {};
      }
      // finding a node where add_segments is true, which means segment creation is allowed in that node,
      // and we will use that node id to fetch section permissions
      const segmentCreationNodeIn =
        selectedEntityWorkflowDetails?.flow_display?.node?.find(
          (node) => node?.data?.add_segments
        ) || EMPTY_OBJECT;

      // find the workflow group which has this node, and will set this
      const workflowGroupOfSegmentCreationNodeIn =
        segmentCreationNodeIn?.parentId || null;
      return {
        segmentCreationNode: segmentCreationNodeIn,
        workflowGroupOfSegmentCreationNode:
          workflowGroupOfSegmentCreationNodeIn,
      };
    }, [selectedEntityWorkflowDetails]);

  // Fetch form details
  useEffect(() => {
    if (!entityData?.entity_id || !entityData?.entity_type) {
      return;
    }
    getCustomEntityTemplate(
      {
        entityId: entityData?.entity_id,
        entityType: entityData?.entity_type,
      },
      false
    ).then((res) => {
      if (res?.data) {
        setFormDetails(res.data);
      }
    });
  }, [entityData?.entity_id, entityData?.entity_type]);

  useEffect(() => {
    if (!segmentCreationNode?.id) {
      return;
    }
    // fetch section permisssions detail for the node where segment creation is allowed,
    handleGetSectionPermission(
      {
        entityId: selectedEntityDetails?.entity_id,
        entityType: selectedEntityDetails?.entity_type,
        workflowState: segmentCreationNode?.id,
      },
      false
    );
  }, [selectedEntityDetails, segmentCreationNode]);

  useEffect(() => {
    // check for workflow groups for the selected entity and,
    // workflow group of the node where segment creation is allowed,
    if (
      isValueEmpty(selectedEntityWorkflowGroups) ||
      isValueEmpty(selectedEntityDetails) ||
      !workflowGroupOfSegmentCreationNode
    ) {
      return;
    }
    // find the group and set its id in common state
    const matchedGroup = selectedEntityWorkflowGroups.find(
      (group) => group?.group_name === workflowGroupOfSegmentCreationNode
    );

    if (matchedGroup) {
      dispatch(
        setCurrentWorkflowGroupId({
          entity_id: selectedEntityDetails.entity_id,
          data: matchedGroup?.workflow_group_id,
        })
      );
    }
  }, [
    selectedEntityWorkflowGroups,
    workflowGroupOfSegmentCreationNode,
    selectedEntityDetails,
  ]);

  // Fetch form details and entity details for the selected entity
  useEffect(() => {
    // early return if we don't have the entity information to fetch details
    if (
      !selectEntityValue ||
      typeof selectEntityValue !== 'object' ||
      isValueEmpty(selectEntityValue?.entity_id) ||
      isValueEmpty(selectEntityValue?.entity_type)
    ) {
      return;
    }

    const selectedEntityId = selectEntityValue?.entity_id;
    const selectedEntityType = selectEntityValue?.entity_type;
    const requestType = selectEntityValue?.request_type;

    // identifier to track changes
    const currentIdentifier = `${selectedEntityId}-${selectedEntityType}-${requestType}`;

    // Only fetch if identifier has changed
    if (currentIdentifier !== prevEntityIdRef.current) {
      getSingleEntityDetails(
        {
          entityId: selectedEntityId,
          entityType: selectedEntityType,
        },
        false
      ).then((res) => {
        if (res?.data?.modelEntity) {
          setSelectedEntityDetails(res.data.modelEntity);

          // fetch workflow data
          getSingleWorkflow({
            params: {
              workflowType: res.data.modelEntity?.workflow,
            },
          });
        }
      });

      // Fetch Template
      getCustomEntityTemplate(
        {
          entityId: selectedEntityId,
          entityType: selectedEntityType || 'ModelInventory',
        },
        false
      ).then((res) => {
        if (res?.data) {
          setFetchedFormDetails(res.data);
        }
      });

      // fetch segments
      getSegments(
        {
          entity_id: selectedEntityId,
          entity_type: selectedEntityType,
        },
        false
      )
        .then((res) => {
          setFetchedSegments(res?.data || []);
        })
        .catch((err) => {
          console.error('Failed to fetch segments', err);
        });

      // Fetch the linked model's full attribute data (including hidden sections)
      // so the segment modal has correct source values for constraint filtering
      // and doesn't autofill subprocess data.
      getAttributeDetails(
        {
          entity_id: selectedEntityId,
          entity_type: selectedEntityType || 'ModelInventory',
        },
        false
      )
        .then((res) => {
          if (res?.data) {
            setModelAttributeDetails(res.data);
          }
        })
        .catch((err) => {
          console.error('Failed to fetch model attribute details', err);
        });

      // Update the ref
      prevEntityIdRef.current = currentIdentifier;
    }
  }, [selectEntityValue]);

  const onSuccess = useCallback(
    (segmentData) => {
      const dataToSave = {
        ...segmentData,
        entity_id: selectedEntityDetails?.entity_id,
        entity_type: selectedEntityDetails?.entity_type,
      };

      setTempSegments((prev) => {
        let newSegments = [...prev];

        if (modalMode === 'edit' || modalMode === 'update') {
          const index = newSegments?.findIndex(
            (s) => s?.segment_id === dataToSave?.segment_id
          );
          if (index > -1) {
            newSegments[index] = dataToSave;
          } else {
            newSegments?.push(dataToSave);
          }
        } else {
          // create, copy, replace -> treat as new entry in the list
          newSegments?.push(dataToSave);
        }
        return newSegments;
      });

      setIsCreateModalOpen(false);
      // Re-open action modal to show updated list?
      setIsActionModalOpen(true);
    },
    [modalMode, selectedEntityDetails]
  );

  const handleViewSegment = useCallback((segment) => {
    handleCloseActionModal();
    setSelectedSegment(segment);
    setModalMode('view');
    setIsCreateModalOpen(true);
  }, []);

  const handleEditSegment = useCallback((segment) => {
    handleCloseActionModal();
    setSelectedSegment(segment);
    setModalMode('edit');
    setIsCreateModalOpen(true);
  }, []);

  const handleUpdateSegment = useCallback(() => {
    handleCloseActionModal();
    setSelectedSegment(null);
    setModalMode('update');
    setIsCreateModalOpen(true);
  }, [
    handleCloseActionModal,
    setSelectedSegment,
    setModalMode,
    setIsCreateModalOpen,
  ]);

  const handleDeleteSegment = useCallback((segment) => {
    setSegmentToDelete(segment);
    setDeleteModalOpen(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!segmentToDelete) return;

    setTempSegments((prev) => prev?.filter((s) => s !== segmentToDelete));
    setDeleteModalOpen(false);
    setSegmentToDelete(null);
    SwalToast({
      title: 'Segment deleted successfully',
      icon: 'success',
    });
  }, [segmentToDelete]);

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setSegmentToDelete(null);
  }, []);

  const handleSave = useCallback(() => {
    const payload = {
      segmentTable: tempSegments,
    };
    onChange(payload);
    onDraftBlur?.();
    setIsActionModalOpen(false);
  }, [onChange, onDraftBlur, tempSegments]);

  const handleOnMenuExit = useCallback(() => {
    setSelectedRow(null);
  }, [setSelectedRow]);

  const handleGetCellProps = useCallback(
    (cell) => {
      return {
        onContextMenu: (e) => {
          e.preventDefault();
          setSelectedRow(cell?.row);
          showRowLevelMenus({
            id: 'segments-table-menus',
            event: e,
            props: cell?.row,
          });
        },
        style: {
          backgroundColor:
            cell?.row?.original?.segment_id ===
            selectedRow?.original?.segment_id
              ? theme.palette.action.hover
              : 'inherit',
          cursor: 'pointer',
        },
      };
    },
    [showRowLevelMenus, selectedRow, theme]
  );

  const menus = useMemo(() => {
    const baseMenus = [
      {
        label: 'View',
        id: 'view',
        icon: VisibilityIcon,
        onClick: ({ props }) => {
          // eslint-disable-next-line react/prop-types
          handleViewSegment(props?.original);
        },
        isMenuDisabled: !isUserAuthorizedForUpdateAttribute && !viewOnly,
      },
      {
        label: 'Edit',
        id: 'edit',
        icon: EditIcon,
        onClick: ({ props }) => {
          // eslint-disable-next-line react/prop-types
          handleEditSegment(props?.original);
        },
        isMenuDisabled: !isUserAuthorizedForUpdateAttribute,
      },
      {
        label: 'Delete',
        id: 'delete',
        icon: DeleteIcon,
        onClick: ({ props }) => {
          // eslint-disable-next-line react/prop-types
          handleDeleteSegment(props?.original);
        },
        isMenuDisabled: !isUserAuthorizedForUpdateAttribute,
      },
    ];

    if (viewOnly) {
      return baseMenus.filter((m) => m.id === 'view');
    }
    return baseMenus;
  }, [handleViewSegment, handleEditSegment, handleDeleteSegment, viewOnly]);

  const handleClose = useCallback(() => {
    setIsCreateModalOpen(false);
    // Re-open action modal when closing create modal?
    setIsActionModalOpen(true);
  }, [setIsCreateModalOpen, setIsActionModalOpen]);

  const handleUpdateEntity = useCallback(() => {
    // This function can be used to trigger any updates needed
    return Promise.resolve();
  }, []);

  const isEntityMismatch = useMemo(() => {
    if (tempSegments?.length > 0) {
      const firstEntityId = tempSegments[0]?.entity_id;
      if (
        firstEntityId &&
        selectedEntityDetails?.entity_id &&
        firstEntityId !== selectedEntityDetails?.entity_id
      ) {
        return true;
      }
    }
    return false;
  }, [tempSegments, selectedEntityDetails?.entity_id]);

  // Check if segment creation is explicitly disabled for this entity
  const isSegmentCreationDisabled = useMemo(() => {
    // Only return true if selectedEntityDetails is loaded AND flag is explicitly false
    return (
      selectedEntityDetails &&
      selectedEntityDetails?.is_models_created_from_segment === false
    );
  }, [selectedEntityDetails?.is_models_created_from_segment]);

  // Determine if buttons should be disabled
  const shouldDisableSegmentButtons = useMemo(() => {
    return isEntityMismatch || isSegmentCreationDisabled;
  }, [isEntityMismatch, isSegmentCreationDisabled]);

  const hasUpdatableSegments = useMemo(
    () =>
      fetchedSegments?.some((seg) => seg?.segment_id !== DEFAULT_SEGMENT_ID),
    [fetchedSegments]
  );

  const modalSegmentsData = useMemo(
    () => [...(fetchedSegments || []), ...(tempSegments || [])],
    [fetchedSegments, tempSegments]
  );

  const segmentActionTooltip = useMemo(() => {
    if (isEntityMismatch) return 'Segment is already created with other entity';
    if (isSegmentCreationDisabled)
      return 'Segment creation is not allowed for this entity';
    return '';
  }, [isEntityMismatch, isSegmentCreationDisabled]);

  const updateSegmentTooltip = useMemo(() => {
    if (isEntityMismatch) return 'Segment is already created with other entity';
    if (isSegmentCreationDisabled)
      return 'Segment creation is not allowed for this entity';
    if (!hasUpdatableSegments) return 'No segments available to update';
    return '';
  }, [isEntityMismatch, isSegmentCreationDisabled, hasUpdatableSegments]);

  const modalTitle = useMemo(() => {
    if (modalMode === 'create') return 'Create New Segment';
    if (modalMode === 'replace') return 'Replaced Segment';
    if (modalMode === 'edit') return 'Edit Segment';
    if (modalMode === 'update') return 'Update Segment';
    return 'View Segment';
  }, [modalMode]);

  return (
    <Box>
      {showLabel && (
        <Typography
          variant="body4"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            position: 'relative',
            top: '4px',
            maxWidth: '100%',
            ':hover': {
              fontWeight: 600,
              cursor: 'pointer',
            },
          }}
        >
          {label}
        </Typography>
      )}
      <Box
        onClick={handleFieldClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '36px',
          cursor: 'pointer',
        }}
      >
        <Typography
          sx={{
            color: hasDraftChip ? theme.palette.draft.accent : 'primary.main',
            textDecoration: 'underline',
            fontSize: '14px',
          }}
        >
          {viewOnly
            ? `View ${attribute?.label || 'Segments'}`
            : `Manage ${attribute?.label || 'Segments'}`}
        </Typography>
      </Box>

      {/* Action Selection Dialog */}
      <Dialog
        open={isActionModalOpen}
        onClose={handleCloseActionModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Segments</Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseActionModal}
            sx={{
              color: theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers onClick={hideAll}>
          {!viewOnly && isMultipleEditMode === true && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end',
                mb: 2,
              }}
            >
              <Tooltip title={segmentActionTooltip}>
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddSegment}
                    size="small"
                    disabled={shouldDisableSegmentButtons}
                  >
                    Add Segment
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={segmentActionTooltip}>
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleReplaceSegment}
                    size="small"
                    disabled={shouldDisableSegmentButtons}
                  >
                    Add & Replace Segment
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={updateSegmentTooltip}>
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleUpdateSegment}
                    size="small"
                    disabled={
                      shouldDisableSegmentButtons || !hasUpdatableSegments
                    }
                  >
                    Update Segment
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
          <ReactMuiTableListView
            noSearchResultMessage="No segments added yet."
            data={tempSegments}
            columns={SEGMENTS_COLUMNS}
            enableRowSelection={false}
            pageCount={tempSegments?.length}
            enablePagination={true}
            autoResetExpanded={false}
            isAllOptionDisabled={true}
            initialPageSize={5}
            rowsPerPageOptions={[5, 10, 15]}
            getCellProps={handleGetCellProps}
          />
          <ContextMenus
            menuId="segments-table-menus"
            menus={menus}
            onMenuExit={handleOnMenuExit}
          />
          <DeleteModal
            open={deleteModalOpen}
            handleClose={closeDeleteModal}
            deleteConfirm={confirmDelete}
            resourceName="Segment"
            alertLabelText={`Are you sure you want to delete the segment "${segmentToDelete?.segment_name}"?`}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCloseActionModal}
            sx={{
              color: 'text.secondary',
              borderColor: 'text.secondary',
              '&:hover': {
                borderColor: 'text.primary',
                backgroundColor: 'action.hover',
              },
            }}
          >
            {viewOnly ? 'Close' : 'Cancel'}
          </Button>
          {!viewOnly && (
            <Button variant="contained" onClick={handleSave} color="primary">
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {isCreateModalOpen && (
        <CreateEditSegmentModal
          open={isCreateModalOpen}
          handleClose={handleClose}
          mode={modalMode}
          title={modalTitle}
          segment={selectedSegment}
          formDetails={filteredFormDetails}
          attributeDetails={modelAttributeDetails}
          currentModel={selectedEntityDetails}
          handleUpdateEntity={handleUpdateEntity}
          allUserList={allUserList}
          teamUsersList={teamUsersList}
          groupList={groupList}
          onSuccess={onSuccess}
          deleteNote={deleteNote}
          editNote={editNote}
          handleOpenNoteModal={handleOpenNoteModal}
          handleSetAddNoteModalHeading={handleSetAddNoteModalHeading}
          handleSetSelectedEntityToAddInEditor={
            handleSetSelectedEntityToAddInEditor
          }
          getAttComments={getAttComments}
          allAttributeChats={allAttributeChats}
          breadcrumbDetails={breadcrumbDetails}
          isUserAuthorizedForUpdateAttribute={
            isUserAuthorizedForUpdateAttribute
          }
          getOverrideAttributeValue={getOverrideAttributeValue}
          segmentsData={modalSegmentsData}
          allTemplateData={fetchedFormDetails}
          isSegmentAttribute={true}
        />
      )}
    </Box>
  );
};

SegmentsAttribute.propTypes = {
  label: PropTypes.node,
  showLabel: PropTypes.bool,
  attribute: PropTypes.shape({
    linked_attribute: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  onChange: PropTypes.func,
  entityDetailsFormikForm: PropTypes.shape({
    values: PropTypes.shape({}),
  }),
  entityData: PropTypes.shape({
    entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity_type: PropTypes.string,
  }),
  allUserList: PropTypes.arrayOf(PropTypes.shape({})),
  teamUsersList: PropTypes.arrayOf(PropTypes.shape({})),
  groupList: PropTypes.arrayOf(PropTypes.shape({})),
  isUserAuthorizedForUpdateAttribute: PropTypes.bool,
  breadcrumbDetails: PropTypes.shape({}),
  getOverrideAttributeValue: PropTypes.func,
  deleteNote: PropTypes.func,
  editNote: PropTypes.func,
  handleOpenNoteModal: PropTypes.func,
  handleSetAddNoteModalHeading: PropTypes.func,
  handleSetSelectedEntityToAddInEditor: PropTypes.func,
  getAttComments: PropTypes.func,
  allAttributeChats: PropTypes.arrayOf(PropTypes.shape({})),
  isMultipleEditMode: PropTypes.bool,
  viewOnly: PropTypes.bool,
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
};

export default SegmentsAttribute;
